import React, { Component } from 'react'
import { connect } from 'react-redux'

import Style from './ThreatFeedDetections.module.css'
import ThreatFeedSearchSelect from './ThreatFeedSearchSelect'
import { getUTCDate } from '../../../utilities/datetime'
import { isGlobalView } from '../../../utilities/config'
import { eventTypes } from '../../../utilities/eventTypes'
import { loadEvents, setThreatFiltersInStore } from '../../../store/entities/events'
import { getSearchedExtensionUsers } from '../../../store/entities/customers'

// ===========================================================================
// Threat Feed — filter bar (PIXMC-6500).
//
// All four filters use the shared ThreatFeedSearchSelect: User and Brand are searchable,
// Date range and Status are plain (no search box). Users are loaded server-side
// (getSearchedExtensionUsers → customers.searchedUsers), like the legacy user filter;
// brands come fully from getBrands and are filtered locally. Wiring mirrors the legacy
// IncidentFilter's threat branch and dispatches the same events thunks — isolated UI,
// shared Redux state. Date range includes the 7-day option.
// ===========================================================================

const RANGE_DAYS = { 7: 7, 30: 30, 60: 60, 90: 90, 365: 365, 1095: 1095 }

const RANGE_OPTIONS = [
  { id: '7', value: '7', name: 'Last 7 days' },
  { id: '30', value: '30', name: 'Last 30 days' },
  { id: '60', value: '60', name: 'Last 60 days' },
  { id: '90', value: '90', name: 'Last 90 days' },
  { id: '365', value: '365', name: 'Last 1 year' },
  { id: '1095', value: '1095', name: 'Last 3 years' },
]

const STATUS_OPTIONS = [
  { id: 'all', name: 'Status: All', value: 'all', eventType: [eventTypes.incident, eventTypes.reported_phish, eventTypes.google_form_password] },
  { id: '0', name: 'AI Suspicious', value: '0', eventType: [eventTypes.incident, eventTypes.google_form_password] },
  { id: '3', name: 'PIXM Verified', value: '3', eventType: [] },
  { id: '1', name: 'Verified Phish', value: '1', eventType: [eventTypes.incident, eventTypes.reported_phish] },
]

const ALL_USERS = { id: 'allusers', value: 'allusers', name: 'User: All' }
const ALL_BRANDS = { id: 'allbrands', value: 'allbrands', name: 'Brand: All' }

class ThreatFeedFilter extends Component {
  state = { days: '30', status: 'all', brand: 'allbrands', user: 'allusers' }

  componentDidMount() {
    this.loadUsers('')
  }

  componentDidUpdate(prev) {
    // New customer → new brands/users/data; reset the selectors and reload users.
    if (prev.selectedCustomer !== this.props.selectedCustomer) {
      this.setState({ days: '30', status: 'all', brand: 'allbrands', user: 'allusers' })
      this.loadUsers('')
    }
  }

  // Server-side user search (empty term loads the first page), like the legacy user filter.
  loadUsers = (term = '') => {
    const { selectedCustomer } = this.props
    if (!selectedCustomer) return
    this.props.getSearchedExtensionUsers(
      selectedCustomer.id,
      term,
      selectedCustomer.key,
      isGlobalView(selectedCustomer.organizationName),
    )
  }

  dispatchFilters = (next) => {
    const { selectedCustomer } = this.props
    if (!selectedCustomer) return
    this.props.setThreatFiltersInStore(next)
    this.props.loadEvents(
      next,
      selectedCustomer.id,
      selectedCustomer.defaultCustomerId,
      isGlobalView(selectedCustomer.organizationName),
    )
  }

  // Start from the current filters so selections combine; reset paging on every change.
  base = () => ({ ...this.props.threatFilters, offset: 0, pageSize: 21 })

  onRange = (value) => {
    const next = { ...this.base(), startTime: getUTCDate(RANGE_DAYS[value]), endTime: getUTCDate(0) }
    this.setState({ days: value })
    this.dispatchFilters(next)
  }

  onStatus = (value) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === value)
    const next = { ...this.base(), type: opt.eventType }
    if (value === 'all') {
      delete next.triagedEvent
      delete next.pixmVerified
    } else {
      next.triagedEvent = value
      next.pixmVerified = opt.name === 'PIXM Verified'
    }
    this.setState({ status: value })
    this.dispatchFilters(next)
  }

  onBrand = (value) => {
    const next = this.base()
    if (value === 'allbrands') delete next.brand
    else next.brand = value
    this.setState({ brand: value })
    this.dispatchFilters(next)
  }

  onUser = (value) => {
    const next = this.base()
    if (value === 'allusers') delete next.extensionUserId
    else next.extensionUserId = value
    this.setState({ user: value })
    this.dispatchFilters(next)
  }

  render() {
    const { brandsList, usersList, viewType, onViewSelect } = this.props
    const users = usersList && usersList.length ? usersList : [ALL_USERS]
    const brands = brandsList && brandsList.length ? brandsList : [ALL_BRANDS]

    return (
      <div className={Style.filterBar}>
        <ThreatFeedSearchSelect
          options={users}
          value={this.state.user}
          onChange={this.onUser}
          onSearch={this.loadUsers}
          ariaLabel="Filter by user"
        />
        <ThreatFeedSearchSelect
          options={brands}
          value={this.state.brand}
          onChange={this.onBrand}
          ariaLabel="Filter by brand"
        />
        <ThreatFeedSearchSelect
          options={RANGE_OPTIONS}
          value={this.state.days}
          onChange={this.onRange}
          searchable={false}
          ariaLabel="Date range"
        />
        <ThreatFeedSearchSelect
          options={STATUS_OPTIONS}
          value={this.state.status}
          onChange={this.onStatus}
          searchable={false}
          ariaLabel="Filter by status"
        />

        <div className={Style.viewToggles}>
          <button
            type="button"
            className={`${Style.viewToggle} ${viewType === 'list' ? Style.viewToggleActive : ''}`}
            onClick={() => onViewSelect('list')}
            title="List view"
            aria-label="List view"
          >
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M2 3h12v1H2V3zm0 3h12v1H2V6zm0 3h12v1H2V9zm0 3h12v1H2v-1z" /></svg>
          </button>
          <button
            type="button"
            className={`${Style.viewToggle} ${viewType === 'grid' ? Style.viewToggleActive : ''}`}
            onClick={() => onViewSelect('grid')}
            title="Grid view"
            aria-label="Grid view"
          >
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M1 2h6v6H1V2zm8 0h6v6H9V2zM1 10h6v4H1v-4zm8 0h6v4H9v-4z" /></svg>
          </button>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  selectedCustomer: state.entities.customers.selected,
  brandsList: state.entities.customers.brands,
  usersList: state.entities.customers.searchedUsers,
  threatFilters: state.entities.events.threat.filters,
})

const mapDispatchToProps = (dispatch) => ({
  setThreatFiltersInStore: (filters) => dispatch(setThreatFiltersInStore(filters)),
  loadEvents: (filters, selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(loadEvents(filters, selectedId, defaultCustomerId, isGlobalView)),
  getSearchedExtensionUsers: (selectedId, term, key, isGlobalView) =>
    dispatch(getSearchedExtensionUsers(selectedId, term, key, isGlobalView)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreatFeedFilter)
